import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';

import { RootState, AppThunkConfig } from '../../app/store';
import { Tenant, TenantSummary, TenantProvider, EntityRegisterOptions } from '../../types';
import { bindAPI } from '../../app/auth';
import { generatePath, route_path } from '../../app/route';
import {
  ListTenantsResponse,
  listTenants,
  CreateTenantParams,
  CreateTenantResponse,
  createTenant,
  GetTenantParams,
  GetTenantResponse,
  getTenant,
  UpdateTenantParams,
  UpdateTenantResponse,
  updateTenant,
} from '../../handlers';
import {
  setCurrentTenantId,
} from '../user/userSlice';
import { logoutAction } from '../auth/authSlice';
import { stripEmptyString } from '../../utils';
import { ErrorName } from '../../errors/error-name';

export type TenantState = {
  tenants: TenantSummary[];
  list_status: 'idle'|'loading'|'failed'|'success';
  list_error?: SerializedError;
  get_status: 'idle'|'loading'|'failed'|'success';
  get_result?: ReturnType<typeof getTenantAction.fulfilled>|ReturnType<typeof getTenantAction.rejected>;
  create_status: 'idle'|'loading'|'failed'|'success';
  create_result?: ReturnType<typeof createTenantAction.fulfilled>|ReturnType<typeof createTenantAction.rejected>;
  update_status:  'idle'|'loading'|'failed'|'success';
  update_result?: ReturnType<typeof updateTenantAction.fulfilled>|ReturnType<typeof updateTenantAction.rejected>;
};

const initialState: TenantState = {
  tenants: [],
  list_status: 'idle',
  get_status: 'idle',
  create_status: 'idle',
  update_status: 'idle',
};

export const listTenantsAction = createAsyncThunk<
  ListTenantsResponse, void, AppThunkConfig
>(
  'tenant/list',
  (arg, thunkAPI) => bindAPI(listTenants, thunkAPI)(arg)
);

export type CreateTenantActionParams = CreateTenantParams;
export type CreateTenantActionResponse = CreateTenantResponse;
export const createTenantAction = createAsyncThunk<
  CreateTenantActionResponse, CreateTenantActionParams, AppThunkConfig
>(
  'tenant/create',
  (params, thunkAPI) => bindAPI(createTenant, thunkAPI)(stripEmptyString(params))
);

export type GetTenantActionParams = GetTenantParams;
export type GetTenantActionResponse = GetTenantResponse;
export const getTenantAction = createAsyncThunk<
  GetTenantActionResponse, GetTenantActionParams, AppThunkConfig
>(
  'tenant/get',
  (params, thunkAPI) => bindAPI(getTenant, thunkAPI)(params)
);

export type UpdateTenantActionParams = UpdateTenantParams;
export type UpdateTenantActionResponse = UpdateTenantResponse;
export const updateTenantAction = createAsyncThunk<
  UpdateTenantActionResponse, UpdateTenantActionParams, AppThunkConfig
>(
  'tenant/update',
  (params, thunkAPI) => {
    let get_result = selectGetTenantResult(thunkAPI.getState());
    let update_custom_domains = false;
    if (get_result && getTenantAction.fulfilled.match(get_result)) {
      let original = get_result.payload.custom_domains;
      if (original.length !== params.body.custom_domains?.length) {
        update_custom_domains = true;
      }
      if (!update_custom_domains) {
        params.body.custom_domains?.forEach((custom_domain, index) => {
          if (original[index].fqdn !== custom_domain.fqdn) {
            update_custom_domains = true;
          }
        });
      }
    }
    let body = stripEmptyString(params.body, {
      convert_to_null: true,
    });
    return bindAPI(updateTenant, thunkAPI)({
      tenant_id: params.tenant_id,
      body: {
        ...body,
        custom_domains: update_custom_domains ? body.custom_domains : undefined,
      },
    });
  }
);

export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listTenantsAction.pending, (state) => {
        state.list_status = 'loading';
      })
      .addCase(listTenantsAction.fulfilled, (state, action) => {
        state.list_status = 'success';
        state.tenants = action.payload.data;
      })
      .addCase(listTenantsAction.rejected, (state, action) => {
        state.list_status = 'failed';
        state.list_error = action.error;
      })
      .addCase(getTenantAction.pending, (state) => {
        state.get_status = 'loading';
      })
      .addCase(getTenantAction.fulfilled, (state, action) => {
        state.get_status = 'idle';
        state.get_result = action;
      })
      .addCase(getTenantAction.rejected, (state, action) => {
        state.get_status = 'failed';
        state.get_result = action;
      })
      .addCase(createTenantAction.pending, (state) => {
        state.create_status = 'idle';
      })
      .addCase(createTenantAction.fulfilled, (state, action) => {
        state.create_status = 'success';
        state.create_result = action;
      })
      .addCase(createTenantAction.rejected, (state, action) => {
        state.create_status = 'failed';
        state.create_result = action;
      })
      .addCase(updateTenantAction.pending, (state) => {
        state.update_status = 'idle';
      })
      .addCase(updateTenantAction.fulfilled, (state, action) => {
        state.update_status = 'success';
        state.update_result = action;
      })
      .addCase(updateTenantAction.rejected, (state, action) => {
        state.update_status = 'failed';
        state.update_result = action;
      })
      .addCase(logoutAction, () => initialState)
      .addCase(setCurrentTenantId, (state) => ({
        ...initialState,
        tenants: state.tenants,
      }))
      .addCase(LOCATION_CHANGE, (state) => ({
        ...initialState,
        tenants: state.tenants,
      }));
  },
});

export const selectTenants = (state: RootState) => state.tenant.tenants;
export const selectTenantsError = (state: RootState) => state.tenant.list_error;
export const selectGetTenantResult = (state: RootState) => state.tenant.get_result;
export const selectUpdateTenantResult = (state: RootState) => state.tenant.update_result;

export const getTenantDetailUrl = (tenant_id: string, mode: 'edit'|'view') => {
  if (mode === 'view') {
    return generatePath(route_path.tenant_view, {
      tenant_id,
    });
  }
  if (mode === 'edit') {
    return generatePath(route_path.tenant_edit, {
      tenant_id,
    });
  }
  throw new Error(ErrorName.unknown);
};

export const tenant_field_options: EntityRegisterOptions<Tenant & { without_postfix: boolean }> = {
  title: {
    required: 'Required',
    minLength: {
      value: 4,
      message: 'Length must be at least 4 characters long',
    },
    maxLength: {
      value: 32,
      message: 'length must be less than or equal to 32 characters long',
    },
  },
  name: { 
    required: 'Required',
    minLength: {
      value: 4,
      message: 'Length must be at least 4 characters long',
    },
    maxLength: {
      value: 32,
      message: 'Length must be less than or equal to 32 characters long',
    },
    pattern: {
      value: /^[a-z][a-z-0-9]*$/,
      message: 'Fails to match the required pattern: /^[a-z][a-z-0-9]*$/',
    },
  },
  description: { 
    maxLength: {
      value: 2048,
      message: 'Length must be less than or equal to 2048 characters long',
    },
  },
};

export const tenant_provider_field_options: EntityRegisterOptions<TenantProvider> = {
  name: {
    required: 'Required a provider',
  },
  client_id: {
    required: 'Required',
  },
  client_secret: {
    required: 'Required',
  },
};
